import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import {
  getPipeline,
  listLeads,
  updateLead,
  convertLead,
  deleteLead,
} from "../../services/leadService";
import { useTenantMembers } from "../../hooks/useTenantMembers";
import CreateLeadModal from "../../components/CreateLeadModal/CreateLeadModal";
import LeadDetailDrawer from "../../components/LeadDetailDrawer/LeadDetailDrawer";
import LeadColumn from "../../components/LeadColumn/LeadColumn";
import LeadCard from "../../components/LeadCard/LeadCard";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import { extractZodFieldError } from "../../utils/zodErrors";
import styles from "./Leads.module.css";

// ─── Configuração de estágios ─────────────────────────────────────────
// `accent` é a cor sólida (badges/avatars), `accentSoft` é o fundo da coluna.
const STAGES = [
  { id: "NEW", label: "Novo interesse", emoji: "👋", accent: "#3498db", accentSoft: "#e1efff" },
  { id: "CONTACTED", label: "Já entrei em contato", emoji: "💬", accent: "#f39c12", accentSoft: "#fdf2dd" },
  { id: "QUALIFIED", label: "Tem potencial", emoji: "✅", accent: "#27ae60", accentSoft: "#dff5e6" },
  { id: "PROPOSAL", label: "Proposta enviada", emoji: "📋", accent: "#8e44ad", accentSoft: "#efe1f7" },
  { id: "WON", label: "Virou cliente!", emoji: "🎉", accent: "#16a085", accentSoft: "#d2efe7" },
  { id: "LOST", label: "Não fechou", emoji: "❌", accent: "#7f8c8d", accentSoft: "#ecedee" },
];

const STAGE_LABELS = STAGES.reduce((acc, s) => {
  acc[s.id] = s.label;
  return acc;
}, {});

const MOBILE_BREAKPOINT = 768;

/**
 * Detecta se um erro do axios é um cancelamento (AbortController.abort).
 * Trata-se sempre como no-op (sem toast, sem state update visível).
 */
function isCanceledError(err) {
  return (
    axios.isCancel?.(err) ||
    err?.code === "ERR_CANCELED" ||
    err?.name === "CanceledError" ||
    err?.name === "AbortError"
  );
}

export default function Leads() {
  // Sem gate de plano: o funil de leads é visível nos DOIS planos (Básico e
  // Profissional). O limite de leads continua sendo aplicado pelo backend no
  // POST /api/leads (403 `LIMIT_REACHED`, tratado em `CreateLeadModal`) — o que
  // sai daqui é apenas o gate de *visibilidade*, que `usePlanFeature("leads")`
  // ligava indevidamente enquanto `user.tenant.plan` ainda não tinha hidratado.
  const {
    byUserId: membersByUserId,
    members,
    loading: membersLoading,
    error: membersError,
  } = useTenantMembers();

  const [pipeline, setPipeline] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("ALL");
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < MOBILE_BREAKPOINT : false,
  );

  // Confirm dialog para ações destrutivas/sensíveis vindas do menu [···] do card.
  const [pendingConfirm, setPendingConfirm] = useState(null);

  // Estado dedicado pra transição LOST via menu [···] do card:
  // backend exige `lostReason` quando stage === "LOST" — então abrimos
  // um ConfirmDialog próprio com textarea required em vez de PATCH direto.
  // `lead` aqui é o lead alvo; null = dialog fechado.
  const [pendingLostMove, setPendingLostMove] = useState(null);
  const [lostMoveReason, setLostMoveReason] = useState("");
  const [lostMoveError, setLostMoveError] = useState(null);
  const [movingToLost, setMovingToLost] = useState(false);

  const searchTimer = useRef(null);
  const abortRef = useRef(null);

  // ─── Detecção de viewport ─────────────────────────────────────────
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handler = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // ─── Fetch pipeline (com AbortController) ─────────────────────────
  const fetchPipeline = useCallback(async (searchTerm = "") => {
    // Cancela request anterior em flight para evitar race condition (resposta antiga sobrescrevendo a nova).
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      setLoading(true);
      setError(null);
      if (searchTerm.trim()) {
        // Busca usa /api/leads?search=... e agrupa client-side por stage
        const res = await listLeads(
          { search: searchTerm.trim() },
          { signal: controller.signal },
        );
        const grouped = STAGES.reduce((acc, s) => {
          acc[s.id] = [];
          return acc;
        }, {});
        const items = Array.isArray(res?.data) ? res.data : res?.data?.leads || [];
        items.forEach((lead) => {
          if (grouped[lead.stage]) grouped[lead.stage].push(lead);
        });
        setPipeline(grouped);
      } else {
        const res = await getPipeline({ signal: controller.signal });
        // Garante que todos os stages existam, mesmo vazios
        const data = res?.data || {};
        const normalized = STAGES.reduce((acc, s) => {
          acc[s.id] = Array.isArray(data[s.id]) ? data[s.id] : [];
          return acc;
        }, {});
        setPipeline(normalized);
      }
    } catch (err) {
      if (isCanceledError(err)) {
        // Request foi cancelada por uma nova busca em sequência — no-op intencional.
        return;
      }
      setError("Não conseguimos carregar seus leads agora. Tente novamente em instantes.");
      toast.error("Erro ao carregar leads.");
    } finally {
      // Só desliga o loading se este controller ainda é o atual
      // (caso contrário, uma request mais recente já está cuidando do estado).
      if (abortRef.current === controller) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchPipeline();
    // Cleanup: aborta qualquer request em flight ao desmontar a tela.
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchPipeline]);

  // ─── Busca com debounce ───────────────────────────────────────────
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchPipeline(value), 350);
  };

  // ─── Ações sobre leads ────────────────────────────────────────────
  /**
   * Move um lead pra outro estágio.
   *
   * Caso especial: backend exige `lostReason` quando `stage === "LOST"`
   * (validação Zod no `updateLeadSchema.superRefine`). O menu [···] do
   * card não tem prompt nativo de motivo — interceptamos aqui e abrimos
   * um ConfirmDialog com textarea required. Para os demais estágios,
   * fluxo segue direto sem prompt.
   */
  const handleMove = async (lead, toStageId) => {
    if (lead.stage === toStageId) return;

    if (toStageId === "LOST") {
      setLostMoveReason("");
      setLostMoveError(null);
      setPendingLostMove(lead);
      return;
    }

    try {
      await updateLead(lead.id, { stage: toStageId });
      toast.success(`Movido para "${STAGE_LABELS[toStageId]}".`);
      fetchPipeline(search);
    } catch {
      toast.error("Não foi possível mover o lead.");
    }
  };

  /** Atualiza o textarea do dialog de LOST e limpa o erro inline ao digitar. */
  const handleLostMoveReasonChange = (e) => {
    setLostMoveReason(e.target.value);
    if (lostMoveError && e.target.value.trim()) {
      setLostMoveError(null);
    }
  };

  /** Cancela o dialog de LOST do menu [···]: no-op + limpa estado. */
  const cancelLostMove = () => {
    setPendingLostMove(null);
    setLostMoveReason("");
    setLostMoveError(null);
  };

  /**
   * Confirma a transição pra LOST a partir do menu [···] do card.
   * Validação required (defesa em profundidade — UI mostra erro inline
   * em vez de deixar o backend devolver 400 silencioso).
   */
  const confirmLostMove = async () => {
    const lead = pendingLostMove;
    if (!lead) return;
    const reason = lostMoveReason.trim();
    if (!reason) {
      setLostMoveError(
        "Conte o que aconteceu — é importante pra entender o funil.",
      );
      return;
    }
    try {
      setMovingToLost(true);
      await updateLead(lead.id, { stage: "LOST", lostReason: reason });
      toast.success(`Movido para "${STAGE_LABELS.LOST}".`);
      setPendingLostMove(null);
      setLostMoveReason("");
      setLostMoveError(null);
      fetchPipeline(search);
    } catch (err) {
      // Se o backend devolver erro de validação Zod pro campo lostReason,
      // surface a mensagem específica em vez do genérico — UX mais clara.
      const fieldMsg = extractZodFieldError(err, "lostReason");
      if (fieldMsg) {
        toast.error(fieldMsg);
        setLostMoveError(fieldMsg);
      } else {
        toast.error("Não foi possível mover o lead.");
      }
    } finally {
      setMovingToLost(false);
    }
  };

  const handleConvert = async (lead) => {
    try {
      await convertLead(lead.id);
      toast.success(`${lead.name} virou cliente! 🎉`);
      setSelectedLead(null);
      fetchPipeline(search);
    } catch (err) {
      const msg = err?.response?.data?.error || "Não foi possível converter o lead.";
      toast.error(msg);
    }
  };

  const handleDelete = async (lead) => {
    try {
      await deleteLead(lead.id);
      toast.success("Lead removido.");
      setSelectedLead(null);
      fetchPipeline(search);
    } catch {
      toast.error("Não foi possível remover o lead.");
    }
  };

  /**
   * Solicita confirmação via ConfirmDialog (substitui window.confirm).
   * `confirmVariant: "danger"` aplica o styling vermelho para ações destrutivas.
   */
  const requestDeleteConfirm = (lead) => {
    setPendingConfirm({
      title: "Remover este lead?",
      message: `Esta ação não pode ser desfeita. Tem certeza de que quer remover "${lead.name}" do seu funil?`,
      confirmLabel: "Sim, remover",
      cancelLabel: "Cancelar",
      confirmVariant: "danger",
      onConfirm: async () => {
        setPendingConfirm(null);
        await handleDelete(lead);
      },
    });
  };

  // ─── Derived ──────────────────────────────────────────────────────
  const totalLeads = useMemo(
    () => STAGES.reduce((sum, s) => sum + (pipeline[s.id]?.length || 0), 0),
    [pipeline],
  );

  const visibleStages = useMemo(() => {
    if (stageFilter === "ALL") return STAGES;
    return STAGES.filter((s) => s.id === stageFilter);
  }, [stageFilter]);

  // ─── Render: empty state global ──────────────────────────────────
  const isInitialEmpty = !loading && !error && totalLeads === 0 && !search.trim();

  return (
    <div className={styles.container}>
      {/* Topbar */}
      <div className={styles.topbar}>
        <div className={styles.topbarLeft}>
          <h1 className={styles.title}>🎯 Funil de Leads</h1>
          <p className={styles.subtitle}>
            Acompanhe seus potenciais clientes do primeiro contato até a venda.
          </p>
        </div>
        <button
          type="button"
          className={styles.addBtn}
          onClick={() => {
            setEditingLead(null);
            setShowFormModal(true);
          }}
        >
          <span aria-hidden="true">➕</span> Novo Lead
        </button>
      </div>

      {/* Filtros */}
      {!isInitialEmpty && (
        <div className={styles.filters}>
          <div className={styles.searchWrap}>
            <span className={styles.searchIcon} aria-hidden="true">🔍</span>
            <input
              type="search"
              value={search}
              onChange={handleSearchChange}
              placeholder="Buscar por nome, telefone ou e-mail…"
              className={styles.searchInput}
              aria-label="Buscar leads"
            />
          </div>
          <div className={styles.chips} role="tablist" aria-label="Filtrar por estágio">
            <button
              type="button"
              role="tab"
              aria-selected={stageFilter === "ALL"}
              className={`${styles.chip} ${stageFilter === "ALL" ? styles.chipActive : ""}`}
              onClick={() => setStageFilter("ALL")}
            >
              Todos ({totalLeads})
            </button>
            {STAGES.map((s) => {
              const count = pipeline[s.id]?.length || 0;
              return (
                <button
                  key={s.id}
                  type="button"
                  role="tab"
                  aria-selected={stageFilter === s.id}
                  className={`${styles.chip} ${
                    stageFilter === s.id ? styles.chipActive : ""
                  }`}
                  onClick={() => setStageFilter(s.id)}
                  style={
                    stageFilter === s.id
                      ? { background: s.accent, color: "#fff", borderColor: s.accent }
                      : undefined
                  }
                >
                  <span aria-hidden="true">{s.emoji}</span> {s.label} ({count})
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Estado de erro */}
      {error && !loading && (
        <div className={styles.errorBox} role="alert">
          {error}
          <button type="button" className={styles.retryBtn} onClick={() => fetchPipeline(search)}>
            Tentar novamente
          </button>
        </div>
      )}

      {/* Empty state global */}
      {isInitialEmpty && (
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon} aria-hidden="true">⭐</span>
          <h2 className={styles.emptyTitle}>Você ainda não tem leads cadastrados</h2>
          <p className={styles.emptyText}>
            Adicione seu primeiro potencial cliente e comece a acompanhar seu funil de vendas.
          </p>
          <button
            type="button"
            className={styles.addBtn}
            onClick={() => {
              setEditingLead(null);
              setShowFormModal(true);
            }}
          >
            <span aria-hidden="true">➕</span> Adicionar primeiro lead
          </button>
        </div>
      )}

      {/* Pipeline */}
      {!isInitialEmpty && !error && (
        <>
          {isMobile ? (
            <MobileList
              stages={visibleStages}
              pipeline={pipeline}
              loading={loading}
              allStages={STAGES}
              onOpenLead={setSelectedLead}
              onMoveLead={handleMove}
              onDeleteLead={requestDeleteConfirm}
              membersByUserId={membersByUserId}
            />
          ) : (
            <div className={styles.kanban}>
              {visibleStages.map((stage) => (
                <LeadColumn
                  key={stage.id}
                  stage={stage}
                  leads={pipeline[stage.id] || []}
                  loading={loading}
                  stages={STAGES}
                  onOpenLead={setSelectedLead}
                  onMoveLead={handleMove}
                  onDeleteLead={requestDeleteConfirm}
                  membersByUserId={membersByUserId}
                  isDeemphasized={stage.id === "LOST"}
                  collapsible={stage.id === "WON"}
                  defaultCollapsed={stage.id === "WON"}
                />
              ))}
            </div>
          )}

          {/* Busca sem resultados */}
          {!loading && search.trim() && totalLeads === 0 && (
            <p className={styles.noResults}>
              Nenhum lead encontrado para "<strong>{search}</strong>".
            </p>
          )}
        </>
      )}

      {/* Modal de criação/edição */}
      {showFormModal && (
        <CreateLeadModal
          lead={editingLead}
          onClose={() => {
            setShowFormModal(false);
            setEditingLead(null);
          }}
          onSuccess={() => {
            setShowFormModal(false);
            setEditingLead(null);
            fetchPipeline(search);
          }}
        />
      )}

      {/* Drawer de detalhes */}
      {selectedLead && (
        <LeadDetailDrawer
          lead={selectedLead}
          stages={STAGES}
          stageLabels={STAGE_LABELS}
          members={members}
          membersLoading={membersLoading}
          membersError={membersError}
          onClose={() => setSelectedLead(null)}
          onEdit={(lead) => {
            setEditingLead(lead);
            setShowFormModal(true);
          }}
          onConvert={handleConvert}
          onDelete={handleDelete}
          onAfterChange={() => fetchPipeline(search)}
        />
      )}

      {/* Confirm dialog cross-action (substitui window.confirm) */}
      <ConfirmDialog
        isOpen={!!pendingConfirm}
        title={pendingConfirm?.title || ""}
        message={pendingConfirm?.message || ""}
        confirmLabel={pendingConfirm?.confirmLabel || "Confirmar"}
        cancelLabel={pendingConfirm?.cancelLabel || "Cancelar"}
        confirmVariant={pendingConfirm?.confirmVariant || "primary"}
        onConfirm={pendingConfirm?.onConfirm || (() => setPendingConfirm(null))}
        onCancel={() => setPendingConfirm(null)}
      />

      {/* Confirm dialog específico para transição → LOST via menu [···].
          Backend exige `lostReason` (Zod superRefine); UI enforce required. */}
      <ConfirmDialog
        isOpen={!!pendingLostMove}
        title="Por que não fechou?"
        message="Conte o que aconteceu — é importante pra entender o funil."
        confirmLabel={movingToLost ? "Salvando…" : "Marcar como perdido"}
        cancelLabel="Cancelar"
        confirmVariant="primary"
        confirmDisabled={!!pendingLostMove && lostMoveReason.trim().length === 0}
        onConfirm={confirmLostMove}
        onCancel={cancelLostMove}
      >
        {lostMoveError && (
          <p
            id="lost-move-error"
            role="alert"
            className={styles.lostInlineError}
          >
            {lostMoveError}
          </p>
        )}
        <textarea
          className={styles.lostTextarea}
          value={lostMoveReason}
          onChange={handleLostMoveReasonChange}
          placeholder="Ex.: preço acima do esperado, escolheu concorrente, etc."
          rows={3}
          aria-label="Motivo da perda"
          aria-required="true"
          aria-invalid={lostMoveError ? "true" : "false"}
          aria-describedby={lostMoveError ? "lost-move-error" : undefined}
          required
        />
      </ConfirmDialog>
    </div>
  );
}

/* ─── Sub-componente: lista mobile ────────────────────────────────── */
// Etapas que iniciam colapsadas por padrão no mobile (paridade com desktop).
const COLLAPSIBLE_STAGES = new Set(["WON"]);

function MobileList({
  stages,
  pipeline,
  loading,
  allStages,
  onOpenLead,
  onMoveLead,
  onDeleteLead,
  membersByUserId = {},
}) {
  // Map: stageId -> bool (colapsado). Apenas WON inicia true.
  const [collapsedMap, setCollapsedMap] = useState(() =>
    stages.reduce((acc, s) => {
      if (COLLAPSIBLE_STAGES.has(s.id)) acc[s.id] = true;
      return acc;
    }, {}),
  );

  const toggleStage = (stageId) => {
    if (!COLLAPSIBLE_STAGES.has(stageId)) return;
    setCollapsedMap((prev) => ({ ...prev, [stageId]: !prev[stageId] }));
  };

  const handleHeaderKeyDown = (e, stageId) => {
    if (!COLLAPSIBLE_STAGES.has(stageId)) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleStage(stageId);
    }
  };

  return (
    <div className={styles.mobileList}>
      {stages.map((stage) => {
        const leads = pipeline[stage.id] || [];
        const isCollapsible = COLLAPSIBLE_STAGES.has(stage.id);
        const isCollapsed = isCollapsible && collapsedMap[stage.id];
        const bodyId = `mobile-stage-body-${stage.id}`;

        const headerCommonProps = {
          className: `${styles.mobileGroupHeader} ${
            isCollapsible ? styles.mobileGroupHeaderCollapsible : ""
          }`,
          style: {
            background: stage.accentSoft,
            borderLeft: `4px solid ${stage.accent}`,
          },
        };

        const headerInteractiveProps = isCollapsible
          ? {
              role: "button",
              tabIndex: 0,
              onClick: () => toggleStage(stage.id),
              onKeyDown: (e) => handleHeaderKeyDown(e, stage.id),
              "aria-expanded": !isCollapsed,
              "aria-controls": bodyId,
            }
          : {};

        return (
          <section key={stage.id} className={styles.mobileGroup}>
            <header {...headerCommonProps} {...headerInteractiveProps}>
              <span className={styles.mobileGroupTitle}>
                {isCollapsible && (
                  <span className={styles.mobileCollapseIcon} aria-hidden="true">
                    {isCollapsed ? "▶" : "▼"}
                  </span>
                )}
                <span aria-hidden="true">{stage.emoji}</span> {stage.label}
              </span>
              <span className={styles.mobileGroupCount}>{leads.length}</span>
            </header>
            {!isCollapsed && (
              <div className={styles.mobileCards} id={bodyId}>
                {loading && (
                  <div className={styles.mobileSkeleton} aria-hidden="true" />
                )}
                {!loading && leads.length === 0 && (
                  <p className={styles.mobileEmpty}>Nenhum lead aqui ainda.</p>
                )}
                {!loading &&
                  leads.map((lead) => (
                    <LeadCard
                      key={lead.id}
                      lead={lead}
                      assignee={
                        lead.assignedTo
                          ? membersByUserId[lead.assignedTo] || null
                          : null
                      }
                      stages={allStages}
                      currentStage={stage.id}
                      onOpen={onOpenLead}
                      onMove={onMoveLead}
                      onDelete={onDeleteLead}
                    />
                  ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
